import React, { ReactElement } from "react";
import { Col, Row } from "react-bootstrap";
import { Question, UserAnswer } from "./CalculatorContext";
import "./css/style.css";
import Graph from "./Graph";
import { computeCoverage } from "./utils";

const isMobile = window.innerWidth < 600; 

export default function CalculatorResult({
  userAnswers,
  questions,
  translation,
}: {
  userAnswers: UserAnswer[];
  questions: Question[];
  translation: any;
}): ReactElement {
  const { labels, pct } = computeCoverage(questions, userAnswers, translation);
  function summarize(questions: Question[], answers: UserAnswer[]) {
    let totals: Record<string, number> = {};
    let yesPerCat: Record<string, number> = {};
    let no: Array<Question> = [];
    let yes: Array<Question> = [];
    for (const q of questions) {
      const a = answers[q.id - 1].answer === "yes";
      (a ? yes : no).push(q);
      (q.categories || []).forEach((cat) => {
        totals[cat] = (totals[cat] || 0) + 1;
        if (a) yesPerCat[cat] = (yesPerCat[cat] || 0) + 1;
      });
    }
    const cats = Object.keys(totals)
      .map((cat) => {
        const y = yesPerCat[cat] || 0,
          t = totals[cat];
        return {
          key: cat,
          label: translation.category[cat] || cat,
          yes: y,
          total: t,
          pct: Math.round((y * 100) / t),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "cs"));
    return { yes, no, cats };
  }

  const s = summarize(questions, userAnswers);

  return (
    <>
      <header>
        <div className="d-flex flex-column flex-lg-row justify-between">
          <h4 className="font-bold tracking-wide">Report RED (DA) Compliance</h4>
          <div className="result-date">
            {translation.result.date}: {new Date().toLocaleDateString("cs-CZ")}
          </div>
        </div>
      </header>
      <div className="d-flex flex-column flex-sm-row justify-between align-items-start align-sm-items-center gap-3 mt-4">
        <p className="text-slate-400 m-0">{translation.result.text}</p>
        <div className="d-flex gap-2">
          <div className="result result-summary">
            {translation.result.questions_sum}: {questions.length}
          </div>
          <div className="result result-summary result-yes">
            {translation.result.yes}: {s.yes.length}
          </div>
          <div className={`result result-summary result-no`}>
            {translation.result.no}: {s.no.length}
          </div>
        </div>
      </div>
      <hr className="sepparator-horiz-divelit-grey-30 mt-4"></hr>
      <div className="mt-5 d-flex flex-column gap-4 justify-between">
        <div className="w-100">
          <h5 className="mb-4">{translation.result.section_title_coverage}</h5>
          <div className="d-flex flex-wrap gap-3 mt-3 mb-5">
            {s.cats.map((c) => (
              <div
                key={c.key}
                className="result result-summary d-flex align-items-center gap-2 rounded-full px-3 py-1"
              >
                <span
                  className={
                    c.pct >= 80
                      ? "result-indicator result-indicator-low"
                      : c.pct >= 50
                      ? "result-indicator result-indicator-medium"
                      : "result-indicator result-indicator-height"
                  }
                ></span>
                <span>{c.label}</span>
                <strong>{c.pct}%</strong>
                <span className="text-slate-400">
                  ({c.yes}/{c.total})
                </span>
              </div>
            ))}
          </div>
          <Graph
            labels={labels}
            data={[
              {
                label: "Pokrytí kategorií (%)",
                data: pct,
                backgroundColor: "rgb(0, 161, 92, 0.5)",
                borderColor: "#00A15C",
              },
            ]}
            options={{
              responsive: true,
              scales: {
                r: {
                  beginAtZero: true,
                  max: 100,
                  ticks: { stepSize: 20, showLabelBackdrop: false },
                  grid: { color: "#CEC9E1" },
                  angleLines: { color: "#CEC9E1" },
                  pointLabels: { color: "#1A1A1A", font: { size: 18 }, display: !isMobile },
                },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    title: (items: any) => {
                      return items[0].chart.data.labels[items[0].dataIndex];
                    },
                  },
                },
              },
            }}
          />
        </div>
        <div className="w-100">
          <h5 className="mb-4">{translation.result.summary_title}</h5>
          <div className="d-flex flex-wrap gap-2">
            {questions.map((q) => {
              const a = userAnswers[q.id - 1].answer === "yes";
              return (
                <span key={q.id} className={`result ${a ? "result-yes" : "result-no"}`}>
                  {q.id} - {a ? translation.result.yes : translation.result.no}
                </span>
              );
            })}
          </div>
        </div>
      </div>
      <section>
        <h5 className="mt-5 mb-4">{translation.result.section_title_no}</h5>
        <div className="d-flex flex-column gap-3">
          {s.no.map((q) => (
            <div key={q.id} className="result result-card">
              <div className="result-header">
                <div className="d-flex flex-column flex-sm-row justify-between align-items-start align-sm-items-center gap-2">
                  <div className="d-flex flex-wrap justify-between align-items-center gap-2">
                    {q.categories.map((cat) => (
                      <span className="tag">{translation.category[cat]}</span>
                    ))}
                  </div>
                  <span className="result result-no">{translation.result.no}</span>
                </div>
                <h6 className="m-2 pt-3 pb-2 result-question">{q.question}</h6>
              </div>
              <div className="result-answer">
                <div className="m-2">
                  <div className="result-answer-title">{translation.card.infoText}</div>
                  <div className="result-answer-content">{q.infoText || "—"}</div>
                </div>
                <div className="m-2">
                  <div className="result-answer-title">{translation.card.reccommendation}</div>
                  <div className="result-answer-content">{q.recommendation || "—"}</div>
                </div>
                <div className="m-2">
                  <div className="result-answer-title">{translation.card.impact}</div>
                  <div className="result-answer-content">{q.impact || "—"}</div>
                </div>
                <div className="m-2">
                  <div className="result-answer-title">{translation.card.source}</div>
                  <div className="result-answer-content">{q.source || "—"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h5 className="mt-5 mb-4">{translation.result.section_title_yes}</h5>
        <div className="d-flex flex-column gap-3">
          {s.yes.map((q) => (
            <div key={q.id} className="result result-card result-card-yes">
              <div className="d-flex flex-column flex-sm-row justify-between align-items-start align-sm-items-center gap-2">
                <div className="d-flex flex-wrap justify-between align-items-center gap-2">
                  {q.categories.map((cat) => (
                    <span className="tag">{translation.category[cat]}</span>
                  ))}
                </div>
                <span className="result result-yes">{translation.result.yes}</span>
              </div>
              <h6 className="m-2 pt-3 pb-2 result-question">{q.question}</h6>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
